/**
 * 编辑群组页面 - 参考 openlink.chat 设计风格
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { getGroupById, updateGroup } from "../../api/groups";
import { toast } from "sonner";

const updateGroupSchema = z.object({
  group_name: z.string().min(2, "群组名称至少 2 位").max(50, "群组名称最多 50 位"),
  external_group_id: z.string().optional(),
  external_platform: z.enum(["dingtalk", "feishu", "wechat"]).optional(),
});

type UpdateGroupForm = z.infer<typeof updateGroupSchema>;

export default function EditGroup() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [group, setGroup] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<UpdateGroupForm>({
    resolver: zodResolver(updateGroupSchema),
  });

  useEffect(() => {
    loadGroup();
  }, [id]);

  async function loadGroup() {
    if (!id) return;
    try {
      const data = await getGroupById(id);
      setGroup(data);
      setValue("group_name", data.group_name);
      setValue("external_group_id", data.external_group_id || "");
      setValue("external_platform", data.external_platform);
    } catch (error: any) {
      toast.error("加载群组信息失败");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }

  const onSubmit = async (data: UpdateGroupForm) => {
    if (!id) return;
    setIsLoading(true);
    try {
      await updateGroup(id, data);
      toast.success("群组信息已更新");
      navigate(`/groups/${id}`);
    } catch (error: any) {
      // 错误已在 axios 拦截器中处理
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lobster-red"></div>
      </div>
    );
  }

  if (!group) {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-8">
        <a href="/dashboard" className="text-gray-400 hover:text-gray-600" onClick={(e) => { e.preventDefault(); navigate(`/groups/${id}`); }}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </a>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">编辑群组</h1>
          <p className="text-gray-500 mt-1">修改群组信息</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">群组信息</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* 群组 ID（只读） */}
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-2">群组 ID</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 text-sm font-mono break-all">
                {group.group_id}
              </code>
            </div>
            <p className="mt-1 text-xs text-gray-500">群组 ID 不可修改</p>
          </div>

          {/* 群组名称 */}
          <div>
            <label htmlFor="group_name" className="block text-sm font-medium text-gray-700 mb-2">
              群组名称 <span className="text-red-500">*</span>
            </label>
            <input
              id="group_name"
              type="text"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                errors.group_name ? "border-red-500" : "border-gray-300"
              }`}
              {...register("group_name")}
              placeholder="例如：我的机器人集群"
            />
            {errors.group_name && (
              <p className="mt-1 text-sm text-red-600">{errors.group_name.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">2-50 个字符</p>
          </div>

          {/* 平台选择 */}
          <div>
            <label htmlFor="external_platform" className="block text-sm font-medium text-gray-700 mb-2">
              平台类型
            </label>
            <select
              id="external_platform"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                errors.external_platform ? "border-red-500" : "border-gray-300"
              }`}
              {...register("external_platform")}
            >
              <option value="">选择平台</option>
              <option value="dingtalk">钉钉</option>
              <option value="feishu">飞书</option>
              <option value="wechat">企业微信</option>
            </select>
            {errors.external_platform && (
              <p className="mt-1 text-sm text-red-600">{errors.external_platform.message}</p>
            )}
          </div>

          {/* 外部群组 ID */}
          <div>
            <label htmlFor="external_group_id" className="block text-sm font-medium text-gray-700 mb-2">
              外部群组 ID
            </label>
            <input
              id="external_group_id"
              type="text"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                errors.external_group_id ? "border-red-500" : "border-gray-300"
              }`}
              {...register("external_group_id")}
              placeholder="例如：钉钉群 ID"
            />
            {errors.external_group_id && (
              <p className="mt-1 text-sm text-red-600">{errors.external_group_id.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">绑定真实的 IM 群组 ID（可选）</p>
          </div>

          {/* 提交按钮 */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-lobster-red text-white py-3 rounded-lg font-medium hover:bg-lobster-red/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "保存中..." : "保存修改"}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/groups/${id}`)}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              取消
            </button>
          </div>
        </form>
      </div>

      {/* 提示信息 */}
      <div className="mt-6 p-6 bg-blue-50 rounded-xl border border-blue-100">
        <h3 className="text-lg font-bold text-blue-900 mb-3">使用建议</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start"><span className="mr-2">•</span><span>群组 ID 不可修改</span></li>
          <li className="flex items-start"><span className="mr-2">•</span><span>修改平台类型不会影响已添加的机器人</span></li>
          <li className="flex items-start"><span className="mr-2">•</span><span>外部群组 ID 用于绑定真实的 IM 群组</span></li>
        </ul>
      </div>
    </div>
  );
}
